import { ReactElement } from 'react';

import { AxiosError, AxiosProgressEvent, AxiosResponse } from 'axios';
import { toast } from 'react-toastify';

import { createDownloadFileHttpClient } from '@/api';
import { ToastNotification } from '@/components';
import { API_BASE_URLS } from '@/constants';
import { showServerErrorToast } from '@/utils';

export type DownloadFileParams = {
  token: string;
  isNewTab?: boolean;
  fileUrl?: string | ReactElement;
  linkUrl?: string;
  baseApiUrl?: string;
  callbackFunction?: () => void;
  progressFunction?: (progress: number) => void;
};

const handleDirectDownload = (fileUrl: string, isNewTab?: DownloadFileParams['isNewTab']) => {
  if (isNewTab) {
    const newWindow = window.open();

    if (newWindow) {
      newWindow.location.href = fileUrl;
    }

    return;
  }

  window.location.href = fileUrl;
};

const downloadBlobFile = (response: AxiosResponse) => {
  const headerValue = response.headers['content-disposition'];
  const filename = decodeURI(headerValue?.split(';')[1]?.split('=')[1]?.replace(/"/g, ''));

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');

  try {
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
  } finally {
    window.URL.revokeObjectURL(url);
    link.remove();
  }
};

const handleDownloadError = (error: AxiosError, callbackFunction?: DownloadFileParams['callbackFunction']) => {
  if ((error?.response?.status === 500 || error?.status === 500) && error?.isAxiosError && !error.response) {
    showServerErrorToast();
  } else {
    toast(
      <ToastNotification
        type="warning"
        title="Exporting Failed"
        message="Unfortunately, the report generation is taking longer than expected. We are still processing it. Please refresh the page and try downloading again in 5 minutes."
      />,
    );
  }

  callbackFunction?.();
};

export const downloadFile = async ({
  token,
  isNewTab,
  fileUrl,
  linkUrl = '',
  baseApiUrl = '',
  callbackFunction,
  progressFunction,
}: DownloadFileParams) => {
  if (fileUrl) {
    handleDirectDownload(fileUrl.toString(), isNewTab);
    return;
  }

  const apiBaseUrl = baseApiUrl || API_BASE_URLS.DATA_MANAGEMENT_API_URL;
  const downloadFileHttpClient = createDownloadFileHttpClient(apiBaseUrl);

  // inject token
  downloadFileHttpClient.interceptors.request.use((config) => {
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  try {
    downloadFileHttpClient
      .get(linkUrl, {
        onDownloadProgress: ({ total, loaded }: AxiosProgressEvent) => {
          progressFunction?.(Math.round((loaded * 100) / (total ?? 1)));
        },
        responseType: 'blob',
      })
      .then((response) => {
        downloadBlobFile(response);
        callbackFunction?.();
      });
  } catch (error) {
    handleDownloadError(error as AxiosError, callbackFunction);
  }
};
